// Bild-Slot einer Objektmappen-Zeile. Eine Kaskade, überall gleich:
//
//   Objektfoto  →  typ-spezifisches Symbol  →  generisches Kategorie-Icon
//
// Das Foto ist das erste Bild-Dokument am Risikoobjekt (risk-objects/thumbs).
// Fehlt es, zeigt Kfz die Fahrzeug-Silhouette der `vehicleClass` und Gebäude/PV
// ihr Kategorie-Icon — so ist die Zeile nie leer und der Typ bleibt auf einen
// Blick erkennbar.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { Icons } from "./riskIcons";
import {
  VehicleSilhouette,
  vehicleIconKeyFor,
} from "../risikoKfzStep/vehicleSilhouettes";
import { carLogoFor } from "./carLogos";

const useStyles = makeStyles({
  wrap: {
    width: "96px",
    height: "68px",
    flexShrink: 0,
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: "#f6f2f7",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
  },
  img: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  icon: {
    width: "34px",
    height: "34px",
    color: BRAND.muted,
    display: "inline-flex",
  },
  silhouette: {
    color: BRAND.ink,
    opacity: 0.72,
    display: "inline-flex",
  },
  /* Markenlogo klein über der Silhouette — die Marke steht zusätzlich als Text
     in der Zeile, das Logo dient nur der schnellen Wiedererkennung. */
  logo: {
    position: "absolute",
    top: "3px",
    right: "3px",
    width: "22px",
    height: "22px",
    objectFit: "contain",
  },
});

export type RiskThumbKind = "gebaeude" | "pv" | "kfz" | "sonstiges";

export function RiskThumb({
  kind,
  photoUrl,
  vehicleClass,
  manufacturer,
  alt,
}: {
  kind: RiskThumbKind;
  /** Signierte URL des Objektfotos, falls vorhanden. */
  photoUrl?: string | null;
  /** Nur Kfz: Merkmal `vehicleClass` → Silhouette. */
  vehicleClass?: string | null;
  /** Nur Kfz: Merkmal `manufacturer` → Markenlogo (falls hinterlegt). */
  manufacturer?: string | null;
  alt?: string;
}) {
  const styles = useStyles();
  const logo = kind === "kfz" ? carLogoFor(manufacturer) : null;

  if (photoUrl) {
    return (
      <div className={styles.wrap}>
        <img className={styles.img} src={photoUrl} alt={alt ?? ""} loading="lazy" />
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      {kind === "kfz" ? (
        <span className={styles.silhouette}>
          <VehicleSilhouette iconKey={vehicleIconKeyFor(vehicleClass)} size={62} />
        </span>
      ) : (
        <span className={styles.icon}>
          {kind === "gebaeude" ? <Icons.house /> : kind === "pv" ? <Icons.sun /> : <Icons.box />}
        </span>
      )}
      {logo && <img className={styles.logo} src={logo} alt={manufacturer ?? ""} loading="lazy" />}
    </div>
  );
}
